import { Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Select, Space, Tag, Typography } from '@douyinfe/semi-ui';
import { api, queryKeys } from './api';
import {
  buildTranslationExamples,
  effectiveTranslationModels,
  hasCapabilityProfile,
  isDirectionSelectionAvailable,
  modelSourceLanguages,
  modelTargetLanguages,
  translationCapabilityURLs
} from './translationDocs';
import type { BaseURL, EntranceEffectiveCapabilities } from './types';

const { Text } = Typography;

type EntranceHTTPDocsProps = {
  base: BaseURL;
  chatPath: string;
  modelsPath: string;
  requiresKey: boolean;
  chatCurlExample: string;
  chatPythonExample: string;
  renderCodeSnippet: (title: string, value: string) => ReactNode;
  renderCopyableURL: (value: string) => ReactNode;
};

export function EntranceHTTPDocs({
  base,
  chatPath,
  modelsPath,
  requiresKey,
  chatCurlExample,
  chatPythonExample,
  renderCodeSnippet,
  renderCopyableURL
}: EntranceHTTPDocsProps) {
  const queryClient = useQueryClient();
  const capabilities = useQuery({
    queryKey: queryKeys.effectiveCapabilities(base.id),
    queryFn: () => api.effectiveCapabilities(base.id),
    enabled: Boolean(base.id),
    retry: false
  });
  const refresh = useMutation({
    mutationFn: () => api.effectiveCapabilities(base.id, true),
    onSuccess: (data) => queryClient.setQueryData(queryKeys.effectiveCapabilities(base.id), data)
  });
  const view = capabilities.data;
  const translationModels = useMemo(() => effectiveTranslationModels(view), [view]);
  const translationCapabilityPaths = useMemo(
    () => translationCapabilityURLs(modelsPath, translationModels),
    [modelsPath, translationModels]
  );
  const chatAvailable = hasCapabilityProfile(view, 'chat');
  const translationAvailable = hasCapabilityProfile(view, 'translation');
  const hasTranslationProfile = Boolean(view?.profiles.some((profile) => profile.task === 'translation'));
  const tasks = [chatAvailable ? 'chat' : '', translationAvailable ? 'translation' : ''].filter(Boolean) as Array<'chat' | 'translation'>;
  const [activeTask, setActiveTask] = useState<'chat' | 'translation'>('chat');
  const [modelID, setModelID] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');

  useEffect(() => {
    if (tasks.length > 0 && !tasks.includes(activeTask)) setActiveTask(tasks[0]);
  }, [activeTask, tasks.join('|')]);
  useEffect(() => {
    const ids = translationModels.map((model) => model.id);
    if (!ids.includes(modelID)) setModelID(ids[0] || '');
  }, [modelID, translationModels]);

  const selectedModel = translationModels.find((model) => model.id === modelID) || translationModels[0];
  const sources = useMemo(() => modelSourceLanguages(selectedModel), [selectedModel]);
  useEffect(() => {
    const codes = sources.map((language) => language.code);
    if (!codes.includes(sourceLanguage)) setSourceLanguage(codes.includes('auto') ? 'auto' : codes[0] || '');
  }, [sourceLanguage, sources]);
  const targets = useMemo(() => modelTargetLanguages(selectedModel, sourceLanguage), [selectedModel, sourceLanguage]);
  useEffect(() => {
    const codes = targets.map((language) => language.code);
    if (!codes.includes(targetLanguage)) setTargetLanguage(codes[0] || '');
  }, [targetLanguage, targets]);

  const translationExamples = selectedModel && sourceLanguage && targetLanguage
    ? buildTranslationExamples({
        chatCompletionsURL: chatPath,
        model: selectedModel.id,
        sourceLanguage,
        targetLanguage,
        requiresKey,
        appBossBillingEnabled: Boolean(base.appbossBillingEnabled)
      })
    : undefined;

  return (
    <div className="entrance-http-docs">
      <div className="api-doc-section">
        <div className="api-doc-code-head">
          <Space>
            <Text strong>有效能力与常用路径</Text>
            <CapabilityStatusTag loading={capabilities.isLoading} failed={capabilities.isError} view={view} />
          </Space>
          <Button size="small" loading={refresh.isPending} onClick={() => refresh.mutate()}>刷新能力</Button>
        </div>
        <div className="api-doc-path-list">
          {renderCopyableURL(chatPath)}
          {renderCopyableURL(modelsPath)}
          {translationCapabilityPaths.map((path) => <Fragment key={path}>{renderCopyableURL(path)}</Fragment>)}
        </div>
        {(translationAvailable || hasTranslationProfile) && (
          <Text type="tertiary">翻译模型列表与模型能力是公开只读元数据，无需 Bearer 客户端 key 或 X-APPBOSS-UID，不会触发余额检查、扣费或翻译算力；Chat Completions 等业务调用仍按入口配置鉴权和计费。</Text>
        )}
        {!translationAvailable && !hasTranslationProfile && (capabilities.isLoading || capabilities.isError) && (
          <Text type="tertiary">若该入口提供翻译能力，模型列表与模型能力为公开只读元数据；当前模型能力尚未确认，因此不会生成占位或猜测的 capabilities 地址。</Text>
        )}
      </div>

      {capabilities.isLoading && <Banner type="info" closeIcon={null} description="正在沿入口路由读取有效模型、语言方向和限制…" />}
      {capabilities.isError && (
        <Banner
          type="danger"
          closeIcon={null}
          description={`有效能力暂不可用：${safeDashboardError(capabilities.error)}。不会使用静态模型或语言表代替。`}
        />
      )}
      {refresh.isError && <Banner type="warning" closeIcon={null} description={`刷新失败：${safeDashboardError(refresh.error)}`} />}
      {view?.freshness.state === 'stale' && (
        <Banner
          type="warning"
          closeIcon={null}
          description={`当前展示的是有界旧缓存${view.freshness.fetchedAt ? `（抓取时间：${formatCapabilityTime(view.freshness.fetchedAt)}）` : ''}。请刷新或检查下游发现接口；业务请求不受本次刷新操作影响。`}
        />
      )}
      {(view?.warnings || []).map((warning) => <Banner key={warning} type="warning" closeIcon={null} description={warning} />)}

      {tasks.length > 1 && (
        <Space className="api-doc-task-tabs">
          <Button theme={activeTask === 'chat' ? 'solid' : 'borderless'} type="primary" onClick={() => setActiveTask('chat')}>普通对话</Button>
          <Button theme={activeTask === 'translation' ? 'solid' : 'borderless'} type="primary" onClick={() => setActiveTask('translation')}>文本翻译</Button>
        </Space>
      )}

      {chatAvailable && activeTask === 'chat' && (
        <div className="api-doc-task-panel" data-task="chat">
          {renderCodeSnippet('HTTP Chat Completions 示例', chatCurlExample)}
          {renderCodeSnippet('Python OpenAI SDK 示例', chatPythonExample)}
        </div>
      )}

      {translationAvailable && activeTask === 'translation' && (
        <div className="api-doc-task-panel" data-task="translation">
          <TranslationModelTable models={translationModels} selectedModelID={selectedModel?.id || ''} onSelect={setModelID} />
          {selectedModel && !isDirectionSelectionAvailable(selectedModel) && (
            <Banner type="warning" closeIcon={null} description="该模型没有可证明的共同翻译方向，可能是方向策略不兼容或所有可达目标没有共同方向；因此不生成调用示例。" />
          )}
          {selectedModel && isDirectionSelectionAvailable(selectedModel) && (
            <div className="translation-doc-controls">
              <label>
                <Text strong>对外模型</Text>
                <Select value={selectedModel.id} onChange={(value) => setModelID(String(value))} className="select">
                  {translationModels.map((model) => <Select.Option value={model.id} key={model.id}>{model.id}</Select.Option>)}
                </Select>
              </label>
              <label>
                <Text strong>源语言</Text>
                <Select value={sourceLanguage} onChange={(value) => setSourceLanguage(String(value))} className="select">
                  {sources.map((language) => <Select.Option value={language.code} key={language.code}>{language.name} · {language.code}</Select.Option>)}
                </Select>
              </label>
              <label>
                <Text strong>目标语言</Text>
                <Select value={targetLanguage} onChange={(value) => setTargetLanguage(String(value))} className="select">
                  {targets.map((language) => <Select.Option value={language.code} key={language.code}>{language.name} · {language.code}</Select.Option>)}
                </Select>
              </label>
            </div>
          )}
          {translationExamples && (
            <>
              {renderCodeSnippet('Curl 文本翻译示例', translationExamples.curl)}
              {renderCodeSnippet('Python HTTP 客户端示例', translationExamples.python)}
              {renderCodeSnippet('JavaScript Fetch 示例', translationExamples.javascript)}
            </>
          )}
        </div>
      )}

      {view && tasks.length === 0 && (
        <Banner type="warning" closeIcon={null} description="当前入口没有可保证的聊天或翻译能力。请检查上游能力标签、模型路由、集群成员和回退目标。" />
      )}
      {view && !translationAvailable && view.profiles.some((profile) => profile.task === 'translation') && (
        <Banner type="warning" closeIcon={null} description={view.profiles.find((profile) => profile.task === 'translation')?.warning || '没有共同可用的翻译模型。'} />
      )}
    </div>
  );
}

function CapabilityStatusTag({ loading, failed, view }: { loading: boolean; failed: boolean; view?: EntranceEffectiveCapabilities }) {
  if (loading) return <Tag color="blue">读取中</Tag>;
  if (failed || !view) return <Tag color="red">不可用</Tag>;
  if (view.freshness.state === 'stale') return <Tag color="orange">旧缓存</Tag>;
  if (view.freshness.state === 'fresh') return <Tag color="green">新鲜</Tag>;
  if (hasCapabilityProfile(view, 'chat')) return <Tag color="blue">聊天能力配置</Tag>;
  return <Tag color="grey">无有效能力</Tag>;
}

function TranslationModelTable({ models, selectedModelID, onSelect }: { models: ReturnType<typeof effectiveTranslationModels>; selectedModelID: string; onSelect: (model: string) => void }) {
  return (
    <div className="translation-model-table-wrap">
      <table className="translation-model-table">
        <thead>
          <tr>
            <th>对外模型 ID</th>
            <th>流式</th>
            <th>输入上限</th>
            <th>自动识别源语言</th>
            <th>方向规则</th>
            <th>状态</th>
          </tr>
        </thead>
        <tbody>
          {models.map((model) => (
            <tr key={model.id} className={model.id === selectedModelID ? 'selected' : ''} onClick={() => onSelect(model.id)}>
              <td className="translation-model-id">{model.id}</td>
              <td>{model.capabilities?.streaming ? '支持' : '不支持'}</td>
              <td>{model.capabilities?.constraints?.max_input_tokens ? `${model.capabilities.constraints.max_input_tokens} tokens` : '未声明'}</td>
              <td>{model.capabilities?.source_language_auto_detection ? '支持' : '不支持'}</td>
              <td>{model.capabilities?.direction_policy?.rule || model.capabilities?.direction_policy?.type || '未声明'}</td>
              <td><Tag color={model.freshness.state === 'stale' ? 'orange' : 'green'}>{model.freshness.state === 'stale' ? '旧缓存' : '新鲜'}</Tag></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function safeDashboardError(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  return '请求失败';
}

function formatCapabilityTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
